import React, { FC } from 'react';
import {
    SkeletonBlock,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonResponsiveGrid,
    SkeletonRow,
    SkeletonStack,
    SkeletonSurface
} from 'src/components/widgets/skeleton';
import styled from 'styled-components';

const DashboardSkeletonShell = styled(SkeletonPageShell)`
    max-width: 1600px;
    margin: 0 auto;
    padding-top: 14px;
`;

const SectionHeadSkeleton = styled(SkeletonRow)`
    align-items: center;
    margin-bottom: 11px;
`;

const WidgetsGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    grid-auto-rows: 184px;
    gap: 12px;
`;

const WidgetSkeletonCard = styled(SkeletonSurface)`
    padding: 14px 15px 13px;
    border-radius: 14px;
    height: 100%;
`;

const WidgetSkeletonHead = styled(SkeletonRow)`
    align-items: center;
    margin-bottom: 11px;
`;

const ColumnsSkeleton = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 330px;
    gap: 22px;
    align-items: start;

    @media screen and (max-width: 1080px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const UsageSkeletonCard = styled(SkeletonSurface)`
    padding: 14px 15px;
    border-radius: 14px;
    min-height: 96px;
`;

const PanelSkeleton = styled(SkeletonSurface)`
    border-radius: 14px;
    padding: 0;
`;

const DividerSkeleton = styled.hr`
    border: 0;
    border-top: 1px solid #e4e2dc;
    margin: 30px 0;
`;

const PanelRowSkeleton = styled(SkeletonRow)`
    align-items: flex-start;
    padding: 12px 14px;

    & + & {
        border-top: 1px solid #e4e2dc;
    }
`;

const PlanSkeleton = styled.div`
    background: #efeee9;
    border-radius: 14px;
    padding: 14px 16px;
`;

const ResourcesSkeleton = styled(SkeletonRow)`
    justify-content: flex-end;
    flex-wrap: wrap;
    min-width: 0;
`;

const PlanAreaSkeleton = styled.div`
    margin-top: 22px;
`;

export const DashboardPageSkeleton: FC = () => (
    <DashboardSkeletonShell aria-hidden='true'>
        <SkeletonPageHeader $margin='0 0 14px'>
            <SkeletonRow $gap='12px' $align='center'>
                <SkeletonBlock $width='min(260px, 100%)' $height='24px' />
                <SkeletonBlock $width='min(300px, 100%)' $height='16px' />
            </SkeletonRow>
            <ResourcesSkeleton $gap='5px'>
                {Array.from({ length: 6 }).map((_, index) => (
                    <SkeletonBlock key={index} $width={index % 2 === 0 ? '112px' : '94px'} $height='22px' $radius='999px' />
                ))}
            </ResourcesSkeleton>
        </SkeletonPageHeader>

        <ColumnsSkeleton>
            <div>
                <SectionHeadSkeleton $gap='12px'>
                    <SkeletonBlock $width='130px' $height='18px' />
                </SectionHeadSkeleton>
                <WidgetsGridSkeleton>
                    {Array.from({ length: 5 }).map((_, index) => (
                        <WidgetSkeletonCard key={index}>
                            <WidgetSkeletonHead $gap='9px'>
                                <SkeletonBlock $width='28px' $height='28px' $radius='8px' />
                                <SkeletonBlock $width={index % 2 === 0 ? '120px' : '92px'} $height='14px' />
                            </WidgetSkeletonHead>
                            <SkeletonStack $gap='10px'>
                                <SkeletonBlock $width='64px' $height='26px' />
                                <SkeletonBlock $width='85%' $height='16px' $radius='999px' />
                            </SkeletonStack>
                        </WidgetSkeletonCard>
                    ))}
                </WidgetsGridSkeleton>
            </div>
            <div>
                <SectionHeadSkeleton $gap='12px'>
                    <SkeletonBlock $width='140px' $height='18px' />
                </SectionHeadSkeleton>
                <PanelSkeleton>
                    {Array.from({ length: 3 }).map((_, index) => (
                        <PanelRowSkeleton key={index} $gap='10px'>
                            <SkeletonBlock $width='26px' $height='26px' $radius='7px' />
                            <SkeletonStack $gap='6px' $flex='1'>
                                <SkeletonBlock $width={index === 1 ? '80%' : '65%'} $height='13px' />
                                <SkeletonBlock $width='90%' $height='11px' />
                            </SkeletonStack>
                            <SkeletonBlock $width='52px' $height='13px' />
                        </PanelRowSkeleton>
                    ))}
                </PanelSkeleton>
                <PlanAreaSkeleton>
                    <SectionHeadSkeleton $gap='12px'>
                        <SkeletonBlock $width='100px' $height='18px' />
                    </SectionHeadSkeleton>
                    <PlanSkeleton>
                        <SkeletonRow $gap='10px' $justify='space-between' $margin='0 0 13px'>
                            <SkeletonBlock $width='130px' $height='16px' />
                            <SkeletonBlock $width='60px' $height='13px' />
                        </SkeletonRow>
                        <SkeletonResponsiveGrid $columns='repeat(auto-fit, minmax(128px, 1fr))' $gap='16px' $tabletColumns='repeat(2, minmax(0, 1fr))'>
                            {Array.from({ length: 4 }).map((_, index) => (
                                <SkeletonStack key={index} $gap='6px'>
                                    <SkeletonBlock $width='100%' $height='12px' />
                                    <SkeletonBlock $width='100%' $height='4px' $radius='3px' />
                                </SkeletonStack>
                            ))}
                        </SkeletonResponsiveGrid>
                    </PlanSkeleton>
                </PlanAreaSkeleton>
            </div>
        </ColumnsSkeleton>

        <DividerSkeleton />

        <div>
            <SectionHeadSkeleton $gap='12px' $justify='space-between'>
                <SkeletonBlock $width='90px' $height='18px' />
                <SkeletonBlock $width='150px' $height='28px' $radius='8px' />
            </SectionHeadSkeleton>
            <SkeletonResponsiveGrid $columns='repeat(auto-fit, minmax(190px, 1fr))' $gap='12px' $tabletColumns='repeat(2, minmax(0, 1fr))'>
                {Array.from({ length: 4 }).map((_, index) => (
                    <UsageSkeletonCard key={index}>
                        <SkeletonStack $gap='9px'>
                            <SkeletonBlock $width='70%' $height='13px' />
                            <SkeletonBlock $width='80px' $height='24px' />
                            <SkeletonBlock $width='60%' $height='11px' />
                        </SkeletonStack>
                    </UsageSkeletonCard>
                ))}
            </SkeletonResponsiveGrid>
        </div>
    </DashboardSkeletonShell>
);
